/**
 * Get memorable forms colors from user color
 */
export const createUserColors = (baseHexColor) => {
    const { h: baseHue } = getHSLFromHex(baseHexColor);
    const isDullModeValue = isDullMode(baseHexColor);
    const palette = isDullModeValue ? getDullPalette() : getLightPalette(baseHue);
    return {
        textBrand: palette.forms.text.brand,
        textIntense: palette.forms.text.intense,
        textModerate: palette.forms.text.moderate,
        surfaceQuiet: palette.forms.surface.quiet,
        surfaceModerate: palette.forms.surface.moderate,
        surfaceSupershy: palette.forms.surface.supershy,
        borderQuiet: palette.forms.border.quiet,
        borderModerate: palette.forms.border.moderate,
        isDullMode: isDullModeValue,
    };
};
export function getHSLFromHex(hex) {
    // Remove the '#' if present
    hex = hex.replace("#", "");
    // Parse hex values to get red, green, and blue components
    const red = parseInt(hex.substring(0, 2), 16) / 255;
    const green = parseInt(hex.substring(2, 4), 16) / 255;
    const blue = parseInt(hex.substring(4, 6), 16) / 255;
    // Find the maximum and minimum values to calculate lightness
    const maxColorValue = Math.max(red, green, blue);
    const minColorValue = Math.min(red, green, blue);
    let hue = 0;
    let saturation = 0;
    const lightness = (maxColorValue + minColorValue) / 2;
    // Calculate hue and saturation only if max and min are not equal
    if (maxColorValue !== minColorValue) {
        const colorDifference = maxColorValue - minColorValue;
        // Calculate saturation based on lightness
        saturation =
            lightness > 0.5
                ? colorDifference / (2 - maxColorValue - minColorValue)
                : colorDifference / (maxColorValue + minColorValue);
        // Determine hue based on which color is the max value
        switch (maxColorValue) {
            case red:
                hue = (green - blue) / colorDifference + (green < blue ? 6 : 0);
                break;
            case green:
                hue = (blue - red) / colorDifference + 2;
                break;
            case blue:
                hue = (red - green) / colorDifference + 4;
                break;
        }
        hue /= 6; // Normalize hue to be in the range [0, 1]
    }
    // Convert hue, saturation, and lightness to 0-360, 0-100, and 0-100 ranges respectively
    return {
        h: Math.round(hue * 360),
        s: Math.round(saturation * 100),
        l: Math.round(lightness * 100),
    };
}
export function hsl(hue, saturation, lightness) {
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}
/**
 * Helper function for HSL to RGB conversion
 */
function hue2rgb(p, q, t) {
    if (t < 0) {
        t += 1;
    }
    if (t > 1) {
        t -= 1;
    }
    if (t < 1 / 6) {
        return p + (q - p) * 6 * t;
    }
    if (t < 1 / 2) {
        return q;
    }
    if (t < 2 / 3) {
        return p + (q - p) * (2 / 3 - t) * 6;
    }
    return p;
}
/**
 * Convert HSL string to hex format
 */
export function hslToHex(hslString) {
    // Extract h, s, l values from "hsl(h, s%, l%)" format
    const match = hslString.match(/hsl\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*\)/);
    if (!match) {
        return hslString; // Return original if not HSL format
    }
    const h = parseInt(match[1]) / 360;
    const s = parseInt(match[2]) / 100;
    const l = parseInt(match[3]) / 100;
    let r;
    let g;
    let b;
    if (s === 0) {
        r = g = b = l; // achromatic
    }
    else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }
    const toHex = (c) => {
        const hex = Math.round(c * 255).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}
export const getDullPalette = () => {
    return {
        background: {
            default: hsl(0, 0, 100),
            reverse: "black",
        },
        forms: {
            surface: {
                intense: hsl(0, 0, 40),
                moderate: hsl(0, 0, 84),
                quiet: hsl(0, 0, 93),
                supershy: hsl(0, 0, 100),
                reverse: hsl(0, 0, 100),
                error: hsl(357, 100, 96),
                hover: {
                    intense: hsl(0, 0, 34),
                    moderate: hsl(0, 0, 78),
                    quiet: hsl(0, 0, 94),
                },
            },
            text: {
                intense: hsl(0, 0, 14),
                moderate: hsl(0, 0, 21),
                brand: hsl(0, 0, 38),
                supershy: hsl(0, 0, 76),
                reverse: hsl(0, 0, 100),
                error: hsl(356, 82, 61),
            },
            border: {
                intense: hsl(0, 0, 82),
                moderate: hsl(0, 0, 87),
                brand: hsl(0, 0, 48),
                quiet: hsl(0, 0, 82),
                supershy: hsl(0, 0, 95),
                error: hsl(356, 100, 61),
            },
        },
    };
};
export const getLightPalette = (baseHue) => {
    return {
        background: {
            default: hsl(baseHue, 100, 97),
            reverse: "black",
        },
        forms: {
            surface: {
                intense: hsl(baseHue, 80, 40),
                moderate: hsl(baseHue, 68, 84),
                quiet: hsl(baseHue, 80, 93),
                supershy: hsl(baseHue, 100, 97),
                reverse: hsl(0, 0, 100),
                error: hsl(357, 100, 96),
                hover: {
                    intense: hsl(baseHue, 86, 34),
                    moderate: hsl(baseHue, 68, 78),
                    quiet: hsl(baseHue, 100, 94),
                },
            },
            text: {
                intense: hsl(baseHue, 74, 14),
                moderate: hsl(baseHue, 74, 21),
                brand: hsl(baseHue, 86, 38),
                supershy: hsl(0, 0, 76),
                reverse: hsl(0, 0, 100),
                error: hsl(356, 82, 61),
            },
            border: {
                intense: hsl(baseHue, 39, 82),
                moderate: hsl(baseHue, 57, 87),
                brand: hsl(baseHue, 84, 48),
                quiet: hsl(baseHue, 83, 82),
                supershy: hsl(baseHue, 58, 95),
                error: hsl(356, 100, 61),
            },
        },
    };
};
export const getDullDarkPalette = () => {
    return {
        background: {
            default: hsl(0, 0, 0),
            reverse: "white",
        },
        forms: {
            surface: {
                intense: hsl(0, 0, 0),
                moderate: hsl(0, 0, 28),
                quiet: hsl(0, 0, 22),
                supershy: hsl(0, 0, 37),
                reverse: hsl(0, 0, 100),
                error: hsl(357, 81, 29),
                hover: {
                    intense: hsl(0, 0, 78),
                    moderate: hsl(0, 0, 84),
                    quiet: hsl(0, 0, 44),
                },
            },
            text: {
                intense: hsl(0, 0, 100),
                moderate: hsl(0, 0, 93),
                brand: hsl(0, 0, 82),
                supershy: hsl(0, 0, 62),
                reverse: hsl(0, 0, 6),
                error: hsl(356, 82, 61),
            },
            border: {
                intense: hsl(0, 0, 100),
                moderate: hsl(0, 0, 61),
                brand: hsl(0, 0, 76),
                quiet: hsl(0, 0, 38),
                supershy: hsl(0, 0, 44),
                error: hsl(356, 100, 61),
            },
        },
    };
};
export const getDarkPalette = (baseHue) => {
    return {
        background: {
            default: hsl(baseHue, 100, 20),
            reverse: "white",
        },
        forms: {
            surface: {
                intense: hsl(baseHue, 100, 20),
                moderate: hsl(baseHue, 64, 28),
                quiet: hsl(baseHue, 51, 33),
                supershy: hsl(baseHue, 43, 37),
                reverse: hsl(baseHue, 100, 76),
                error: hsl(357, 81, 29),
                hover: {
                    intense: hsl(baseHue, 68, 78),
                    moderate: hsl(baseHue, 95, 84),
                    quiet: hsl(baseHue, 50, 44),
                },
            },
            text: {
                intense: hsl(baseHue, 0, 100),
                moderate: hsl(baseHue, 24, 93),
                brand: hsl(baseHue, 100, 82),
                supershy: hsl(baseHue, 25, 62),
                reverse: hsl(baseHue, 100, 6),
                error: hsl(356, 82, 61),
            },
            border: {
                intense: hsl(0, 0, 100),
                moderate: hsl(baseHue, 25, 61),
                brand: hsl(baseHue, 100, 76),
                quiet: hsl(baseHue, 60, 38),
                supershy: hsl(baseHue, 32, 44),
                error: hsl(356, 100, 61),
            },
        },
    };
};
export const isDullMode = (baseHexColor) => {
    return baseHexColor.toLowerCase() === "#ffffff" || baseHexColor === "#000000";
};
/**
 * Converts a hex color to RGB values
 */
export const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
        }
        : null;
};
/**
 * Calculates the relative luminance of a color
 * Based on WCAG guidelines: https://www.w3.org/WAI/GL/wiki/Relative_luminance
 */
const getLuminance = (r, g, b) => {
    const [rs, gs, bs] = [r, g, b].map((c) => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};
/**
 * Determines if text should be black or white based on background color
 * Uses WCAG contrast ratio guidelines for accessibility
 *
 * @param backgroundColor - Hex color string (e.g., "#ffffff" or "ffffff")
 * @returns "black" for light backgrounds, "white" for dark backgrounds
 */
export const getTextColorOfHexColor = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) {
        return "black";
    }
    // Calculate luminance
    const luminance = getLuminance(rgb.r, rgb.g, rgb.b);
    // Return black for light backgrounds (luminance > 0.5), white for dark
    return luminance > 0.5 ? "black" : "white";
};
